import { ORCHARD_CHARM_ATLAS, ORCHARD_BURST_ATLAS } from './40-jewel-artwork.mjs';
const NS = 'http://www.w3.org/2000/svg';

/** Presentation-only shards, power trails and earned score follow actual cleared
 * cells. Registered nodes/animations belong to playback and are removed on
 * cancellation through register(node, cleanup). Native Canvas uses a100-unit
 * board plane, bounded particles/targets and one finite frame chain; unavailable
 * Canvas/animation APIs retain SVG presentation. No geometry supplies scoring. */
export function createGemEffects(ctx, board, playfield, register, animate) {
  const rasters = new Map();
  function raster(url) {
    if (typeof window.Image !== 'function') return null;
    if (!rasters.has(url)) {
      const image = new window.Image();
      image.src = url;
      rasters.set(url, image);
    }
    const image = rasters.get(url);
    return image.complete && image.naturalWidth > 0 ? image : null;
  }
  function layer() {
    const rect = board.getBoundingClientRect(),
      parent = playfield.getBoundingClientRect();
    const svg =
      typeof document.createElementNS === 'function'
        ? document.createElementNS(NS, 'svg')
        : ctx.e('svg');
    svg.setAttribute('class', 'game-gem-effects');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.left = `${rect.left - parent.left}px`;
    svg.style.top = `${rect.top - parent.top}px`;
    svg.style.width = `${rect.width}px`;
    svg.style.height = `${rect.height}px`;
    playfield.append(svg);
    register(svg);
    return svg;
  }
  function shape(host, tag, attrs) {
    const node =
      typeof document.createElementNS === 'function'
        ? document.createElementNS(NS, tag)
        : ctx.e(tag);
    for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
    host.append(node);
    return node;
  }
  function geometry(event) {
    const columns = Math.sqrt(event.boardBefore.length);
    if (!Number.isInteger(columns) || columns < 1) return null;
    const rect = board.getBoundingClientRect();
    const point = (index) => {
      const cell = board.children[index]?.getBoundingClientRect?.();
      if (cell && rect.width > cell.width && rect.height > cell.height)
        return [
          ((cell.left + cell.width / 2 - rect.left) / rect.width) * 100,
          ((cell.top + cell.height / 2 - rect.top) / rect.height) * 100,
        ];
      return [
        ((index % columns) + 0.5) * (100 / columns),
        (Math.floor(index / columns) + 0.5) * (100 / columns),
      ];
    };
    return { point, unit: 100 / columns };
  }
  function burst(host, index, colour, { point, unit }) {
    const [x, y] = point(index),
      tip = unit * 0.09;
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3,
        dx = Math.cos(angle) * unit * 0.55,
        dy = Math.sin(angle) * unit * 0.55;
      const shard = shape(host, 'path', {
        d: `M${x} ${y - tip}L${x + tip * 0.8} ${y}L${x} ${y + tip}L${x - tip * 0.8} ${y}Z`,
        fill: colour,
        stroke: 'var(--game-white)',
        'stroke-width': 0.2,
      });
      shard.style.transformBox = 'fill-box';
      shard.style.transformOrigin = 'center';
      animate(
        shard,
        [
          { opacity: 1, transform: 'translate(0,0) scale(1)' },
          { opacity: 0, transform: `translate(${dx}px,${dy}px) scale(.2)` },
        ],
        360,
      );
    }
  }
  function spectrum(host, effect, point, unit) {
    const [x, y] = point(effect.index);
    for (const target of effect.targets.slice(0, 64)) {
      if (target === effect.index) continue;
      const [tx, ty] = point(target),
        mx = (x + tx) / 2,
        my = (y + ty) / 2;
      const d = `M${x} ${y}L${mx + unit * 0.15} ${my - unit * 0.1}L${mx - unit * 0.1} ${my + unit * 0.15}L${tx} ${ty}`;
      for (const [width, colour] of [
        [unit * 0.15, 'var(--game-colour-2)'],
        [unit * 0.085, 'var(--game-colour-4)'],
        [unit * 0.035, 'var(--game-white)'],
      ]) {
        const ray = shape(host, 'path', {
          d,
          fill: 'none',
          stroke: colour,
          'stroke-width': width,
          'stroke-linejoin': 'round',
          'data-effect': 'spectrum',
        });
        animate(
          ray,
          [
            { opacity: 0 },
            { opacity: 0.9, offset: 0.18 },
            { opacity: 0.8, offset: 0.6 },
            { opacity: 0 },
          ],
          520,
        );
      }
    }
  }
  function nova(host, effect, point, unit, combined) {
    const [x, y] = point(effect.index);
    for (const [stroke, width, radius] of [
      ['var(--game-colour-3)', unit * 0.12, unit * 0.38],
      ['var(--game-white)', unit * 0.05, unit * 0.22],
    ]) {
      const ring = shape(host, 'circle', {
        cx: x,
        cy: y,
        r: radius,
        fill: 'none',
        stroke,
        'stroke-width': width,
        'data-effect': 'nova',
      });
      ring.style.transformOrigin = `${x}px ${y}px`;
      animate(
        ring,
        [
          { opacity: 0.95, transform: 'scale(.4)' },
          { opacity: 0.75, offset: 0.3 },
          { opacity: 0, transform: `scale(${combined ? 6 : 4})` },
        ],
        420,
      );
    }
  }
  function canvasBurst(event, layout) {
    const canvas = document.createElement?.('canvas') ?? ctx.e('canvas');
    const paint = canvas.getContext?.('2d');
    if (!paint || typeof window.requestAnimationFrame !== 'function') return false;
    const rect = board.getBoundingClientRect(),
      parent = playfield.getBoundingClientRect();
    const ratio = Math.min(
      2,
      window.devicePixelRatio || 1,
      1024 / Math.max(rect.width, rect.height),
    );
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    canvas.className = 'game-gem-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    Object.assign(canvas.style, {
      left: `${rect.left - parent.left}px`,
      top: `${rect.top - parent.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    const styles = typeof getComputedStyle === 'function' ? getComputedStyle(board) : null;
    const colours = Array.from(
      { length: 6 },
      (_, index) => styles?.getPropertyValue(`--game-colour-${index}`).trim() || 'white',
    );
    const white = styles?.getPropertyValue('--game-white').trim() || 'white';
    const particles = [],
      bolts = [],
      cores = [];
    const { point, unit } = layout;
    const centres = event.cells.map(point);
    const centre = [0, 1].map(
      (axis) => centres.reduce((sum, p) => sum + p[axis], 0) / centres.length,
    );
    function seeds(index, count, colour, force = 1) {
      const [x, y] = point(index);
      for (let i = 0; i < count && particles.length < 160; i++) {
        const angle = ((i + (index % 7) * 0.21) * Math.PI * 2) / count;
        const speed = unit * (0.45 + ((i * 17 + index * 3) % 11) / 11) * force;
        particles.push({
          x,
          y,
          dx: Math.cos(angle) * speed,
          dy: Math.sin(angle) * speed,
          radius: unit * (i % 3 === 0 ? 0.09 : 0.035),
          colour,
          sharp: i % 3 === 0,
          sprite: Math.max(0, colours.indexOf(colour)),
        });
      }
    }
    for (const index of event.cells.slice(0, 24)) {
      seeds(index, 9, colours[event.boardBefore[index]]);
      cores.push({ point: point(index), colour: colours[event.boardBefore[index]], power: false });
    }
    for (const effect of (event.effectTargets || []).slice(0, 8)) {
      cores.push({
        point: point(effect.index),
        colour: effect.kind === 'spectrum' ? colours[4] : colours[3],
        power: true,
      });
      if (effect.kind === 'spectrum') {
        for (const target of effect.targets.slice(0, 24)) {
          if (target === effect.index || bolts.length >= 48) continue;
          bolts.push({ from: point(effect.index), to: point(target), salt: target + effect.index });
        }
      } else seeds(effect.index, 32, colours[3], 2.1);
    }
    canvas.dataset.particleCount = String(particles.length);
    canvas.dataset.boltCount = String(bolts.length);
    let frame = 0,
      start = null,
      stopped = false;
    function stop() {
      stopped = true;
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    }
    function glow(x, y, radius, colour, opacity) {
      paint.globalAlpha = opacity;
      const gradient = paint.createRadialGradient(x, y, 0, x, y, Math.max(0.01, radius));
      gradient.addColorStop(0, white);
      gradient.addColorStop(0.16, colour);
      gradient.addColorStop(1, 'transparent');
      paint.fillStyle = gradient;
      paint.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    }
    function lightning(bolt, age, opacity) {
      const [x, y] = bolt.from,
        [tx, ty] = bolt.to;
      const length = Math.hypot(tx - x, ty - y) || 1;
      const normal = [-(ty - y) / length, (tx - x) / length];
      const points = Array.from({ length: 8 }, (_, index) => {
        const part = index / 7;
        const offset =
          index === 0 || index === 7
            ? 0
            : Math.sin(index * 13 + bolt.salt + age * 18) * unit * 0.19;
        return [x + (tx - x) * part + normal[0] * offset, y + (ty - y) * part + normal[1] * offset];
      });
      for (const [width, colour, strength] of [
        [unit * 0.22, colours[2], 0.16],
        [unit * 0.09, colours[4], 0.7],
        [unit * 0.023, white, 1],
      ]) {
        paint.beginPath();
        paint.moveTo(...points[0]);
        points.slice(1).forEach((p) => paint.lineTo(...p));
        paint.strokeStyle = colour;
        paint.lineWidth = width;
        paint.globalAlpha = opacity * strength;
        paint.lineJoin = 'round';
        paint.lineCap = 'round';
        paint.stroke();
      }
      // Short illuminated branches terminate inside the actual source-target corridor.
      const branch = points[3];
      paint.beginPath();
      paint.moveTo(...branch);
      paint.lineTo(branch[0] + normal[0] * unit * 0.38, branch[1] + normal[1] * unit * 0.38);
      paint.strokeStyle = white;
      paint.lineWidth = unit * 0.025;
      paint.globalAlpha = opacity * 0.55;
      paint.stroke();
      glow(tx, ty, unit * 0.28, colours[4], opacity * 0.55);
    }
    function draw(time) {
      frame = 0;
      if (
        stopped ||
        document.hidden ||
        !board.isConnected ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
        return stop();
      start ??= time;
      const age = Math.min(1, (time - start) / 520);
      paint.setTransform(1, 0, 0, 1, 0, 0);
      paint.clearRect(0, 0, canvas.width, canvas.height);
      paint.setTransform(canvas.width / 100, 0, 0, canvas.height / 100, 0, 0);
      paint.globalCompositeOperation = 'lighter';
      for (const core of cores) {
        const radius = unit * (core.power ? 0.5 + age * 2.3 : 0.15 + age * 0.65);
        glow(...core.point, radius, core.colour, (1 - age) ** 2 * (core.power ? 0.8 : 0.45));
        if (core.power) {
          paint.beginPath();
          paint.arc(...core.point, radius * 0.7, 0, Math.PI * 2);
          paint.strokeStyle = core.colour;
          paint.lineWidth = unit * 0.055;
          paint.globalAlpha = (1 - age) ** 2;
          paint.stroke();
        }
      }
      bolts.forEach((bolt) => lightning(bolt, age, Math.sin(age * Math.PI) * (1 - age * 0.6)));
      // Rendered botanical art supplies the body of the celebration; geometric
      // glow remains a light accent. The nine-frame sheet has no scoring role.
      paint.globalCompositeOperation = 'source-over';
      const bloom = raster(ORCHARD_BURST_ATLAS),
        charms = raster(ORCHARD_CHARM_ATLAS);
      if (bloom && typeof paint.drawImage === 'function') {
        const frame = Math.min(8, Math.floor(age * 9)),
          cell = bloom.naturalWidth / 3;
        const size = unit * (event.effectTargets?.length ? 6 : 3.4);
        paint.globalAlpha = 1;
        paint.drawImage(
          bloom,
          (frame % 3) * cell,
          Math.floor(frame / 3) * cell,
          cell,
          cell,
          centre[0] - size / 2,
          centre[1] - size / 2,
          size,
          size,
        );
      }
      for (const particle of particles) {
        const progress = 1 - (1 - age) ** 2;
        const x = particle.x + particle.dx * progress,
          y = particle.y + particle.dy * progress + age * age * unit * 0.3;
        glow(x, y, particle.radius * 4, particle.colour, (1 - age) ** 2 * 0.45);
        paint.globalAlpha = (1 - age) ** 1.4;
        paint.fillStyle = particle.colour;
        if (charms && typeof paint.drawImage === 'function') {
          const cell = charms.naturalWidth / 3,
            size = particle.radius * 7;
          paint.drawImage(
            charms,
            (particle.sprite % 3) * cell,
            Math.floor(particle.sprite / 3) * cell,
            cell,
            cell,
            x - size / 2,
            y - size / 2,
            size,
            size,
          );
        } else if (particle.sharp) {
          paint.save();
          paint.translate(x, y);
          paint.rotate(age * 5 + particle.dx);
          paint.fillRect(
            -particle.radius,
            -particle.radius,
            particle.radius * 2,
            particle.radius * 2,
          );
          paint.restore();
        } else {
          paint.beginPath();
          paint.arc(x, y, particle.radius, 0, Math.PI * 2);
          paint.fill();
        }
      }
      paint.globalAlpha = 1;
      paint.globalCompositeOperation = 'source-over';
      if (age < 1) frame = window.requestAnimationFrame(draw);
      else stop();
    }
    playfield.append(canvas);
    register(canvas, stop);
    frame = window.requestAnimationFrame(draw);
    return true;
  }
  function clear(event) {
    const layout = geometry(event);
    if (!layout || !event.cells.length) return;
    const host = layer(),
      { point, unit } = layout;
    // The cell-clear owner animates every accepted clear; debris is decorative
    // and capped independently so large power clears cannot flood the compositor.
    const painted = canvasBurst(event, layout);
    for (const index of painted ? [] : event.cells.slice(0, 24))
      burst(host, index, `var(--game-colour-${event.boardBefore[index]})`, layout);
    let spectra = 0;
    for (const effect of painted ? [] : (event.effectTargets || []).slice(0, 8)) {
      if (effect.kind === 'spectrum') {
        if (spectra++ < 2) spectrum(host, effect, point, unit);
      } else nova(host, effect, point, unit, event.combination === 'nova-nova');
    }
    const centres = event.cells.map(point),
      x = centres.reduce((sum, value) => sum + value[0], 0) / centres.length,
      y = centres.reduce((sum, value) => sum + value[1], 0) / centres.length;
    const label = shape(host, 'text', {
      x: Math.max(12, Math.min(88, x)),
      y: Math.max(10, Math.min(94, y)),
      'text-anchor': 'middle',
      class: 'game-gem-award',
      'data-match-centre': `${x},${y}`,
    });
    label.textContent = `+${event.points}`;
    if (event.chain > 1) {
      const multiplier = shape(host, 'text', {
        x: Math.max(12, Math.min(88, x)),
        y: Math.max(8, Math.min(86, y - 8)),
        'text-anchor': 'middle',
        class: 'game-gem-multiplier',
      });
      multiplier.textContent = `×${Math.min(event.chain, 3)}`;
      animate(
        multiplier,
        [
          { opacity: 0, transform: 'scale(.7)' },
          { opacity: 1, transform: 'scale(1.12)', offset: 0.2 },
          { opacity: 0, transform: 'scale(1)', offset: 1 },
        ],
        520,
      );
    }
    animate(
      label,
      [
        { opacity: 0, transform: 'translateY(3px) scale(.85)' },
        { opacity: 1, transform: 'translateY(0) scale(1)', offset: 0.2 },
        { opacity: 1, offset: 0.55 },
        { opacity: 0, transform: 'translateY(-8px) scale(1)' },
      ],
      520,
    );
  }
  return { clear };
}
